// Email + password sign-in for WeDelvr (replaces Google OAuth).
import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Lock, ShieldCheck, ArrowRight, Mail, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api, { setToken } from "@/lib/api";
import { useAuth } from "@/lib/auth";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Sign-in failed. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const from = location.state?.from || "/dashboard";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const r = await api.post("/auth/login", { email, password }, { withCredentials: true });
      if (r.data?.access_token) setToken(r.data.access_token);
      if (setUser) setUser(r.data);
      toast.success("Welcome back");
      navigate(from, { replace: true });
    } catch (e2) {
      setErr(formatApiErrorDetail(e2?.response?.data?.detail) || "Sign-in failed.");
    } finally {
      setBusy(false);
    }
  };

  const fillDemo = (which) => {
    if (which === "admin") {
      setEmail("admin@wedelvr.com");
      setPassword("Admin@WeDelvr2026");
    } else {
      setEmail("demo@wedelvr.com");
      setPassword("Demo@WeDelvr2026");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-[#FAFAFA]" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="bg-white border border-zinc-200 p-10">
          <Link to="/" className="text-xs text-zinc-500 hover:text-[#002FA7] uppercase tracking-[0.15em] mb-8 inline-block">← Back to landing</Link>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-[#002FA7] text-white flex items-center justify-center font-bold text-sm">CT</div>
            <div>
              <div className="font-heading font-black text-base tracking-tight">WEDELVR</div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">Control Tower</div>
            </div>
          </div>
          <h1 className="font-heading text-3xl font-black mb-2">Sign in</h1>
          <p className="text-sm text-zinc-500 mb-6">Control tower access requires an email and password.</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="data-label">Email</Label>
              <div className="relative mt-1.5">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <Input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" required className="rounded-none h-10 pl-9 metric" />
              </div>
            </div>
            <div>
              <Label className="data-label">Password</Label>
              <div className="relative mt-1.5">
                <KeyRound className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required minLength={8} className="rounded-none h-10 pl-9 metric" />
              </div>
            </div>

            {err && <div className="bg-[#FEE2E2] border border-[#FCA5A5] text-sm text-[#991B1B] p-3" data-testid="login-error">{err}</div>}

            <Button data-testid="login-submit" type="submit" disabled={busy || !email || !password} className="w-full rounded-none bg-[#002FA7] hover:bg-[#002480] h-11">
              {busy ? "Working..." : <>Sign in <ArrowRight className="w-4 h-4 ml-2" /></>}
            </Button>
          </form>

          <div className="text-xs text-zinc-500 mt-4 text-center">
            Access is invite-only. Contact your administrator to request an account.
          </div>

          {/* Demo credentials helper */}
          <div className="mt-6 pt-6 border-t border-zinc-100">
            <div className="data-label mb-3 flex items-center gap-1.5"><ShieldCheck className="w-3 h-3 text-[#002FA7]" />Demo accounts</div>
            <div className="grid grid-cols-2 gap-2">
              <button data-testid="login-demo-admin" type="button" onClick={() => fillDemo("admin")} className="text-left text-xs bg-zinc-50 hover:bg-zinc-100 border border-zinc-200 px-3 py-2 transition-colors">
                <div className="metric font-medium">Admin</div>
                <div className="metric text-zinc-500 text-[10px]">admin@wedelvr.com</div>
              </button>
              <button data-testid="login-demo-user" type="button" onClick={() => fillDemo("user")} className="text-left text-xs bg-zinc-50 hover:bg-zinc-100 border border-zinc-200 px-3 py-2 transition-colors">
                <div className="metric font-medium">User</div>
                <div className="metric text-zinc-500 text-[10px]">demo@wedelvr.com</div>
              </button>
            </div>
            <p className="text-[10px] text-zinc-400 mt-2 metric">Click to auto-fill credentials.</p>
          </div>
        </div>

        <div className="mt-6 text-xs text-zinc-500 space-y-2">
          <div className="flex items-start gap-2"><Lock className="w-3 h-3 mt-0.5 text-[#002FA7]" /><span>JWT-signed sessions, 7-day expiry. Brute force lockout after 5 failed attempts.</span></div>
          <div className="flex items-start gap-2"><ShieldCheck className="w-3 h-3 mt-0.5 text-[#002FA7]" /><span>Per-customer rate visibility — you only see contracts you're entitled to.</span></div>
        </div>
      </div>
    </div>
  );
}
